import React, { useState, useMemo } from 'react';
import { Select, Row, Col, Spin, Button, Radio, Tabs, Alert, Card, Descriptions } from 'antd';
import Chart from 'react-apexcharts';
import {
  ReloadOutlined, LineChartOutlined, ClockCircleOutlined,
  WarningOutlined, RiseOutlined, CloudServerOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useListServersQuery, useLoadHistoryQuery, usePeakHoursQuery } from '../store/api';
import HistoricalLoadCharts from '../components/HistoricalLoadCharts';
import PeakHoursChart from '../components/PeakHoursChart';

const { Option } = Select;

const HistorialCargas = () => {
  const { data: servidores = [] } = useListServersQuery();
  const [servidorSeleccionado, setServidorSeleccionado] = useState(null);
  const [dias, setDias] = useState(30);
  const [intervalo, setIntervalo] = useState('hora');
  const [vistaActual, setVistaActual] = useState('timeline');

  const loadArgs = { dias, agrupacion: intervalo, id_servidor: servidorSeleccionado || '' };
  const peakArgs = { dias, id_servidor: servidorSeleccionado || '' };
  const skip = servidores.length === 0;

  const { data: loadRaw, isFetching: loading, refetch: refetchLoad } = useLoadHistoryQuery(loadArgs, { skip });
  const { data: peakRaw, isFetching: loadingHoras, refetch: refetchPeak } = usePeakHoursQuery(peakArgs, { skip: skip || vistaActual !== 'horas-pico' });

  const historicos = useMemo(() => loadRaw?.success ? loadRaw.servidores : [], [loadRaw]);
  const estadisticas = loadRaw?.estadisticas || null;
  const horasPico = peakRaw?.success ? peakRaw.servidores : [];

  const servidor = useMemo(() => servidores.find(s => s.id === servidorSeleccionado), [servidores, servidorSeleccionado]);

  const handleRefresh = () => {
    refetchLoad();
    if (vistaActual === 'horas-pico') refetchPeak();
  };

  /* ─── Summary charts (ApexCharts) ─── */
  const summaryData = useMemo(() => {
    if (servidorSeleccionado || historicos.length === 0) return null;
    const sorted = [...historicos].map(srv => {
      const cargas = srv.datos.map(d => d.carga);
      const promedio = cargas.reduce((a, b) => a + b, 0) / cargas.length;
      return { ...srv, promedio, maximo: Math.max(...cargas) };
    }).sort((a, b) => b.promedio - a.promedio).slice(0, 5);

    const atencion = [...historicos].map(srv => {
      const cargas = srv.datos.map(d => d.carga);
      const promedio = cargas.reduce((a, b) => a + b, 0) / cargas.length;
      const picos = cargas.filter(c => c > 2).length;
      return { ...srv, promedio, maximo: Math.max(...cargas), picos, pct: (picos / cargas.length) * 100 };
    }).filter(s => s.promedio > 1 || s.pct > 10).sort((a, b) => b.pct - a.pct).slice(0, 5);

    return { sorted, atencion };
  }, [historicos, servidorSeleccionado]);

  const renderLoading = (msg) => (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <Spin size="large" /><p style={{ marginTop: 16, color: '#94a3b8' }}>{msg}</p>
    </div>
  );

  const renderEmpty = (Icon, msg) => (
    <div className="chart-container" style={{ textAlign: 'center', padding: 60 }}>
      <Icon style={{ fontSize: 48, color: '#94a3b8' }} />
      <p style={{ marginTop: 16, color: '#64748b' }}>{msg}</p>
    </div>
  );

  return (
    <div className="page-container fade-in">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title"><LineChartOutlined style={{ marginRight: 8 }} />Historial de Cargas</h1>
          <div className="page-subtitle">Análisis de rendimiento histórico</div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading} style={{ borderRadius: 8, fontWeight: 600 }}>
          Actualizar
        </Button>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={8}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.5px' }}>Servidor</label>
          <Select style={{ width: '100%' }} placeholder="Todos los servidores" allowClear
            value={servidorSeleccionado} onChange={setServidorSeleccionado} size="large">
            {servidores.map(srv => <Option key={srv.id} value={srv.id}>{srv.nombre} ({srv.ip})</Option>)}
          </Select>
        </Col>
        <Col xs={24} sm={8}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.5px' }}>Período</label>
          <Radio.Group value={dias} onChange={e => setDias(e.target.value)} buttonStyle="solid" size="large">
            <Radio.Button value={1}>Hoy</Radio.Button>
            <Radio.Button value={7}>7d</Radio.Button>
            <Radio.Button value={30}>30d</Radio.Button>
            <Radio.Button value={90}>90d</Radio.Button>
          </Radio.Group>
        </Col>
        <Col xs={24} sm={8}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.5px' }}>Agregación</label>
          <Radio.Group value={intervalo} onChange={e => setIntervalo(e.target.value)} buttonStyle="solid" size="large">
            <Radio.Button value="raw">Detalle</Radio.Button>
            <Radio.Button value="hora">Hora</Radio.Button>
            <Radio.Button value="dia">Día</Radio.Button>
          </Radio.Group>
        </Col>
      </Row>

      {servidor && (
        <Card bordered={false} style={{ marginBottom: 20, borderRadius: 12 }}>
          <Descriptions
            title={<span><CloudServerOutlined style={{ marginRight: 6 }} />{servidor.nombre}</span>}
            bordered size="small" column={{ xxl: 6, xl: 4, lg: 3, md: 2, sm: 1, xs: 1 }}>
            <Descriptions.Item label="IP"><code>{servidor.ip}</code></Descriptions.Item>
            <Descriptions.Item label="Sistema">{servidor.sistema || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Núcleos">{servidor.nucleos || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="RAM">{servidor.ram || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Carga">{servidor.carga || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Latencia">{servidor.latencia ? `${servidor.latencia}ms` : 'N/A'}</Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {estadisticas && (
        <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
          {[
            { label: 'Servidores', value: estadisticas.total_servidores },
            { label: 'Registros', value: estadisticas.total_registros?.toLocaleString() },
            { label: 'Desde', value: estadisticas.fecha_inicio ? dayjs(estadisticas.fecha_inicio).format('DD/MM HH:mm') : '-' },
            { label: 'Hasta', value: estadisticas.fecha_fin ? dayjs(estadisticas.fecha_fin).format('DD/MM HH:mm') : '-' },
          ].map((s, i) => (
            <Col xs={12} sm={6} key={i}>
              <div className="chart-container" style={{ textAlign: 'center', padding: '12px 8px' }}>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>{s.value}</div>
              </div>
            </Col>
          ))}
        </Row>
      )}

      {/* Summary charts with ApexCharts */}
      {summaryData && (
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col xs={24} lg={12}>
            <Card bordered={false} style={{ borderRadius: 12 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                <RiseOutlined style={{ color: '#f43f5e' }} /> Top 5 - Mayor Carga Promedio
              </h3>
              <Chart
                type="bar"
                height={280}
                options={{
                  chart: { toolbar: { show: false } },
                  plotOptions: { bar: { borderRadius: 6, horizontal: false, columnWidth: '55%' } },
                  colors: summaryData.sorted.map(s => s.promedio > 2 ? '#f43f5e' : s.promedio > 1 ? '#f59e0b' : '#10b981'),
                  xaxis: { categories: summaryData.sorted.map(s => s.nombre), labels: { style: { fontSize: '11px' } } },
                  yaxis: { labels: { style: { fontSize: '11px' } } },
                  dataLabels: { enabled: true, formatter: v => v.toFixed(2), style: { fontSize: '11px' } },
                  grid: { borderColor: '#e9edf4' },
                  tooltip: { theme: 'light' },
                  legend: { show: false },
                }}
                series={[
                  { name: 'Promedio', data: summaryData.sorted.map(s => +s.promedio.toFixed(2)) },
                ]}
              />
              {summaryData.sorted[0]?.promedio > 2 && (
                <Alert message={`${summaryData.sorted[0].nombre} tiene carga promedio ${summaryData.sorted[0].promedio.toFixed(2)}`} type="error" showIcon style={{ marginTop: 12, borderRadius: 8 }} />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card bordered={false} style={{ borderRadius: 12 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                <WarningOutlined style={{ color: '#f59e0b' }} /> Servidores que Requieren Atención
              </h3>
              {summaryData.atencion.length === 0 ? (
                <Alert message="Todo en orden" description="Sin alertas en este período" type="success" showIcon style={{ borderRadius: 8 }} />
              ) : (
                <>
                  <Chart
                    type="bar"
                    height={280}
                    options={{
                      chart: { toolbar: { show: false } },
                      plotOptions: { bar: { horizontal: true, borderRadius: 6, barHeight: '55%' } },
                      colors: summaryData.atencion.map(s => s.pct > 50 ? '#f43f5e' : s.pct > 20 ? '#f59e0b' : '#eab308'),
                      xaxis: { max: 100, labels: { style: { fontSize: '11px' } }, title: { text: '%' } },
                      yaxis: { labels: { style: { fontSize: '11px' } } },
                      dataLabels: { enabled: true, formatter: v => `${v}%`, style: { fontSize: '11px' } },
                      grid: { borderColor: '#e9edf4' },
                      tooltip: { theme: 'light' },
                      legend: { show: false },
                    }}
                    series={[{ data: summaryData.atencion.map(s => ({ x: s.nombre, y: +s.pct.toFixed(1) })) }]}
                  />
                  <div style={{ marginTop: 12, fontSize: 12, color: '#94a3b8' }}>
                    {summaryData.atencion.map((s, i) => <div key={i}>• <strong>{s.nombre}</strong>: {s.picos} picos (máx: {s.maximo.toFixed(2)})</div>)}
                  </div>
                </>
              )}
            </Card>
          </Col>
        </Row>
      )}

      <Card bordered={false} style={{ borderRadius: 12 }} bodyStyle={{ padding: '8px 16px 16px' }}>
        <Tabs activeKey={vistaActual} onChange={setVistaActual} size="large" items={[
          {
            key: 'timeline',
            label: <><LineChartOutlined /> Línea de Tiempo</>,
            children: loading ? renderLoading('Cargando datos históricos...')
              : historicos.length === 0 ? renderEmpty(LineChartOutlined, 'No hay datos históricos para este período')
              : historicos.map(srv => <HistoricalLoadCharts key={srv.id} servidor={srv} height={350} />),
          },
          {
            key: 'horas-pico',
            label: <><ClockCircleOutlined /> Horas Pico</>,
            children: loadingHoras ? renderLoading('Analizando horas pico...')
              : horasPico.length === 0 ? renderEmpty(ClockCircleOutlined, 'No hay datos de horas pico')
              : horasPico.map(srv => <PeakHoursChart key={srv.id} servidor={srv} height={350} />),
          },
        ]} />
      </Card>
    </div>
  );
};

export default HistorialCargas;
